/**
 * App Config React Query hooks
 * Fetches application-specific config from Gobodo AppConfig__c.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getRecordsForObject,
  createRecord,
  updateRecord,
} from '../recordStorageService';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';

const APPCONFIG_OBJECT = 'AppConfig__c';

export type ApplicationId = 'cybersweep' | 'credit_management' | 'credit_block';

export function useAppConfig<T = unknown>(
  applicationId: ApplicationId,
  configKey: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['app-config', applicationId, configKey],
    queryFn: async (): Promise<T | null> => {
      const records = await getRecordsForObject(APPCONFIG_OBJECT);
      const match = (records as Record<string, unknown>[]).find(
        r =>
          (r.Application__c ?? r.application) === applicationId &&
          (r.ConfigKey__c ?? r.configKey) === configKey
      );
      if (!match) return null;
      const json = match.ConfigValueJson__c ?? match.configValueJson;
      if (typeof json !== 'string') return json as T;
      try {
        return JSON.parse(json) as T;
      } catch {
        return null;
      }
    },
    enabled: (options?.enabled ?? true) && USE_GOBODO_API && !!applicationId && !!configKey,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAppConfigs(applicationId?: ApplicationId) {
  return useQuery({
    queryKey: ['app-configs', applicationId],
    queryFn: async () => {
      const records = await getRecordsForObject(APPCONFIG_OBJECT);
      let filtered = records as Record<string, unknown>[];
      if (applicationId) {
        filtered = filtered.filter(
          r => (r.Application__c ?? r.application) === applicationId
        );
      }
      return filtered;
    },
    enabled: USE_GOBODO_API,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSaveAppConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      applicationId,
      configKey,
      configValue,
      id,
    }: {
      applicationId: ApplicationId;
      configKey: string;
      configValue: unknown;
      id?: string;
    }) => {
      const json = JSON.stringify(configValue);
      if (id) {
        return updateRecord(
          APPCONFIG_OBJECT,
          id,
          {
            ConfigValueJson__c: json,
          },
          { gobodoPropertyPatch: true }
        );
      }
      return createRecord(APPCONFIG_OBJECT, {
        Application__c: applicationId,
        ConfigKey__c: configKey,
        ConfigValueJson__c: json,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-config'] });
      queryClient.invalidateQueries({ queryKey: ['app-configs'] });
    },
  });
}

export const DEFAULT_CYBERSWEEP_STAGES = [
  'Pending Enrollments',
  'Lacking Requirements',
  'Awaiting Assignment',
  'Pending Checklists',
  'Awaiting Initial Report',
  'Awaiting Final Report',
  'Quarterly Report 1',
  'Quarterly Report 2',
  'Quarterly Report 3',
  'Quarterly Report 4',
];

export function useCyberSweepWorkflowStages(options?: { enabled?: boolean }): string[] {
  const { data } = useAppConfig<string[]>('cybersweep', 'workflow_stages', options);
  return (data && Array.isArray(data) ? data : DEFAULT_CYBERSWEEP_STAGES);
}
